import React from 'react';
import ServiceCard from '../components/ServiceCard';
import FaqItem from '../components/FaqItem';
import { SERVICES } from '../data/constants';

const WHY_ITEMS = [
  { n: '01', t: 'Expertise You Can Trust', d: 'Our team of internationally certified auditors brings 20+ years of experience across manufacturing, food, IT, healthcare and 30+ other industries.' },
  { n: '02', t: 'Tailored Solutions', d: 'We design every audit and certification programme around your specific business context, size and regulatory requirements.' },
  { n: '03', t: 'Commitment to Integrity', d: 'As a NABCB-accredited body, impartiality and independence are non-negotiable. You get objective, credible findings — every time.' },
  { n: '04', t: 'Partnering for Success', d: 'We stay engaged beyond the certificate — supporting your surveillance audits, recertification and continuous improvement journey.' },
];

const STATS = [
  ['20+', 'Years in operation since 2006, serving clients across all major industries in India.'],
  ['NABCB', 'Accredited Type-A Inspection Body under ISO/IEC 17020 — recognised internationally through IAF & ILAC.'],
  ['FSSAI', 'Recognised Hygiene Rating Auditing Agency (HRAA) and Third Party Auditing Agency (TPAA).'],
  ['QCI', 'Empanelled ZED Assessment Agency for the MSME Sustainable Certification Scheme by MoMSME.'],
];

export default function HomePage({ setPage }) {
  const goContact = () => { setPage('Contact'); window.scrollTo(0, 0); };
  const goServices = () => { setPage('Services'); window.scrollTo(0, 0); };

  return (
    <main className="page" id="main-content">

      {/* ── HERO ── */}
      <section className="hero" aria-label="AQMCS — ISO Certification & ZED Assessment India">
        <div className="hero-blob hero-blob-1" aria-hidden="true" />
        <div className="hero-blob hero-blob-2" aria-hidden="true" />
        <div className="hero-blob hero-blob-3" aria-hidden="true" />
        <div className="hero-grid-lines" aria-hidden="true" />
        <div className="hero-inner">
          <div>
            <div className="hero-pill">
              <span className="hero-pill-dot" />
              India's Trusted Inspection &amp; Certification Partner Since 2006
            </div>
            <h1>
              Quality You Can <span className="accent">Assure.</span>
              <br />Standards You Can Trust.
            </h1>
            <p>
              Assure Quality Management Certification Services (AQMCS) — a <strong>NABCB-accredited</strong> inspection body providing expert services in <strong>ZED Assessment</strong>, <strong>Food Safety Audits</strong>, <strong>ISO Certification</strong>, Tender Document Verification and Third Party Audits across India.
            </p>
            <div className="hero-btns">
              <button className="btn-blue" onClick={goServices} aria-label="Explore AQMCS services">
                Explore Our Services
              </button>
              <button className="btn-ghost" onClick={goContact} aria-label="Contact AQMCS">
                Contact Us
              </button>
            </div>
          </div>

          <div className="hero-cards" aria-label="Key services at a glance">
            {[
              { icon: '🏭', val: 'ZED', lbl: 'Assessment for MSMEs' },
              { icon: '🍽️', val: 'Food Safety', lbl: 'FSSAI Audit' },
              { icon: '🔍', val: 'Third Party', lbl: 'Customised Audit' },
            ].map((c, i) => (
              <div key={i} className="hcard" role="img" aria-label={`${c.val} ${c.lbl}`}>
                <div className="hcard-icon">{c.icon}</div>
                <div>
                  <div className="hcard-val">{c.val}</div>
                  <div className="hcard-lbl">{c.lbl}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section className="section" aria-labelledby="services-heading">
        <div className="sec-inner">
          <div className="sec-tag">What We Offer</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h2 className="sec-title" id="services-heading">
                ISO Certification, ZED Assessment, Food Safety &amp; Third Party Audit Services
              </h2>
              <p className="sec-lead">
                From MSME-focused ZED certification to FSSAI food safety audits and ISO management system certification — AQMCS delivers accredited, independent audit and inspection services across India.
              </p>
            </div>
            <button className="btn-navy" onClick={goServices} style={{ flexShrink: 0 }} aria-label="View all AQMCS services">
              View All Services →
            </button>
          </div>
          <div className="services-grid">
            {SERVICES.slice(0, 6).map((s, i) => (
              <ServiceCard key={i} {...s} onLearnMore={goContact} />
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY US ── */}
      <section className="section" aria-labelledby="why-heading">
        <div className="sec-inner">
          <div className="why-grid">
            <div>
              <div className="sec-tag">Why Choose AQMCS</div>
              <h2 className="sec-title" id="why-heading">
                India's Most Trusted NABCB-Accredited Inspection &amp; Certification Body
              </h2>
              <p className="sec-lead" style={{ marginBottom: '2rem' }}>
                With accreditations from NABCB, QCI, FSSAI and empanelment with IOCL and NTPC, AQMCS delivers internationally recognised certification and audit services you can rely on.
              </p>
              <div className="why-items">
                {WHY_ITEMS.map((w, i) => (
                  <div key={i} className="why-item">
                    <div className="why-num" aria-hidden="true">{w.n}</div>
                    <div>
                      <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.25rem' }}>{w.t}</h3>
                      <p>{w.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="why-stats" aria-label="AQMCS credentials and statistics">
              {STATS.map(([n, l], i) => (
                <div key={i} className="wstat">
                  <h3>{n}</h3>
                  <p>{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ (Schema.org FAQPage) ── */}
      <section
        className="section bg-light"
        aria-labelledby="faq-home-heading"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        <div className="sec-inner">
          <div className="sec-tag">Common Questions</div>
          <h2 className="sec-title" id="faq-home-heading">Frequently Asked Questions About ISO &amp; ZED Certification</h2>
          <div className="faq-list">
            {[
              {
                q: 'How long does ISO certification take?',
                a: 'Typical ISO certification takes 3–6 months depending on organisation size, current compliance level and the specific standard. Our free gap analysis provides a precise timeline upfront.',
              },
              {
                q: 'What is ZED Certification and who is eligible?',
                a: 'ZED (Zero Defect Zero Effect) is a government scheme for Indian MSMEs to improve quality and reduce environmental impact. All registered MSMEs manufacturing goods are eligible. As a QCI-empanelled ZED Assessment Agency, AQMCS guides you from registration to certification.',
              },
              {
                q: 'Is AQMCS accredited by NABCB?',
                a: 'Yes. AQMCS is accredited by NABCB as a Type-A Inspection Body in accordance with ISO/IEC 17020. NABCB is a member of IAF, ILAC and APAC, and a signatory to their MLAs/MRAs, making our certifications internationally recognised.',
              },
            ].map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="cta-banner">
        <div className="cta-inner">
          <h2>
            Ready to Get <span className="cta-accent">Certified?</span>
          </h2>
          <p>
            Start your ISO or ZED certification journey today. Our accredited experts are ready to guide you through gap analysis, audit and final award.
          </p>
          <button
            className="btn-blue"
            onClick={goContact}
            style={{ fontSize: '1rem', padding: '1rem 2.5rem' }}
            aria-label="Contact AQMCS to begin certification"
          >
            Contact Us →
          </button>
        </div>
      </div>
    </main>
  );
}